// The Learn ladder's phrase rungs (design: docs/design/phrase-drill-design.md §4).
//
// A phrase card is a collocation or an example sentence added to the tray as a Term of its own. The
// plain word rungs answer themselves on it: against a sentence translation ("The rumour turned out
// to be false.") the generic options read "table / to run / quickly", and the only sentence-shaped
// one is the right one. Shape alone answers the question.
//
// The fix is not one shape but two, because the two kinds of phrase teach different things (§4.3).
// A COLLOCATION is memorized as a unit — "sich beschweren ÜBER" is the preposition and its case —
// so its wrong answers are other collocations. A SENTENCE teaches the word in context, so its
// questions are about the focus word: what it means, and which word fills the blank.
//
// Pure card -> plan, no DOM and no jQuery, exactly like phrase_gap.js — which this builds on rather
// than re-deriving: the gap plan, the sentence test and the focus-form split all live there, and one
// owner per rule is the whole reason that module came out DOM-free.
(function (global) {
	"use strict";

	// A conjugated verb form ("ich beschließe") is stored as a phrase Term too, but it is not a
	// phrase in this sense: it drills person and tense and has its own distractor pools, built
	// server-side from the paradigm. It must never fall into either branch below.
	//
	// Neither must the OTHER verb card type: "fahren, fährt, fuhr, ist gefahren" is multi-word, so
	// RichEntryPersister flags its term as a phrase, but it is a recited list rather than a
	// collocation — and it has its own server-built pools too (LearningService, FormSetDistractors).
	// It reached here only to be turned away by an empty collocPool, which was luck, not a rule:
	// nothing stopped a future pool from arriving and quietly taking these cards' rungs over.
	var VERB_FORM_POS = Util.VERB_FORM_POS;   // the one definition lives in main.js Util

	function gap() { return global.PhraseGap; }

	// Which shape this card's rungs take: null (not a drillable phrase — a word, or a phrase whose
	// source entry could not be found, so it keeps the plain rungs), "colloc" or "sentence".
	//
	// The sentence test is PhraseGap's, and deliberately only PhraseGap's: the server ships the
	// material for both branches and lets this one rule decide, rather than growing a second copy of
	// it that could drift (piece 2 settled the same question the same way for the Practice cue).
	function mode(card) {
		if (!card || !card.phrase || !gap()) { return null; }
		if (Util.isVerbFormCard(card) || Util.isVerbFormsCard(card)) { return null; }
		return gap().isSentence(card.term) ? "sentence" : "colloc";
	}

	function lower(s) { return String(s == null ? "" : s).trim().toLowerCase(); }

	// The phrase-level options for a collocation rung: the server's pool thinned to entries of the
	// SAME kind as the card. That is the half of §4.1's "filter on kind and length" the server
	// cannot do — it has no sentence test — and it matters: a full sentence standing beside four
	// bare collocations is picked out on shape without reading a word of it. The length half is
	// already applied server-side.
	function collocOptions(card) {
		var pool = (card && card.collocPool) || [];
		if (!pool.length) { return []; }
		var wantSentence = gap().isSentence(card.term);
		var seenTerm = {}, seenDef = {}, out = [];
		seenTerm[lower(card.term)] = true;
		seenDef[lower(card.gloss)] = true;   // the card's own meaning is not a wrong answer
		for (var i = 0; i < pool.length; i++) {
			var p = pool[i];
			if (!p || !p.term || !p.definition) { continue; }
			if (gap().isSentence(p.term) !== wantSentence) { continue; }
			if (seenTerm[lower(p.term)] || seenDef[lower(p.definition)]) { continue; }
			seenTerm[lower(p.term)] = true;
			seenDef[lower(p.definition)] = true;
			out.push(p);
		}
		return out;
	}

	// The meaning rung for a SENTENCE: the frame is shown as context and the question is what the
	// focus word means there — never "which of these four English sentences is it", which would mean
	// inventing three plausible sentence translations (§4.2). The options are ordinary word glosses,
	// so every one of them has the same shape and the topic gives nothing away.
	//
	// {label: the headword named in the question, correct: its gloss, pool: [gloss], plan: the gap
	// plan for marking the focus token inside the frame — null when the phrase resolved none}.
	function meaningPlan(card) {
		if (!card || !card.focusGloss || !card.focusLabel) { return null; }
		var pool = [], seen = {};
		seen[lower(card.focusGloss)] = true;
		var src = card.focusGlossPool || [];
		for (var i = 0; i < src.length; i++) {
			if (src[i] && !seen[lower(src[i])]) { seen[lower(src[i])] = true; pool.push(src[i]); }
		}
		if (pool.length < 2) { return null; }   // fewer than three options is not a question
		return {
			label: card.focusLabel,
			correct: card.focusGloss,
			pool: pool,
			plan: card.focusForm ? gap().plan(card.term, card.focusForm) : null
		};
	}

	// The gap-fill rung for a SENTENCE: one frame, the gap filled differently (§4.2). Every option is
	// the whole sentence, because the frame is what makes the question answerable — reading it once
	// and scanning the one word that changes is the exercise.
	//
	// Only for a single-blank frame: a separable verb splits across the clause, and filling two
	// blanks would mean dealing PAIRS of words, which the filler pool cannot honestly supply.
	function gapPlan(card) {
		if (!card || !card.focusForm) { return null; }
		var plan = gap().plan(card.term, card.focusForm);
		if (!plan || plan.gaps.length !== 1) { return null; }
		var fillers = [], seen = {};
		seen[lower(plan.gaps[0])] = true;
		var src = card.focusFormPool || [];
		for (var i = 0; i < src.length; i++) {
			if (src[i] && !seen[lower(src[i])]) { seen[lower(src[i])] = true; fillers.push(src[i]); }
		}
		if (fillers.length < 2) { return null; }
		return { plan: plan, correct: plan.gaps[0], fillers: fillers };
	}

	// The sentence with its one blank filled by `word` — the text of one gap-fill option, and the
	// key the pick is judged on, so it has to be exactly what stands on the button.
	function filled(plan, word) {
		var out = "";
		for (var i = 0; i < plan.segments.length; i++) {
			var seg = plan.segments[i];
			out += seg.gap == null ? seg.text : word;
		}
		return out;
	}

	// What the typing rungs ask for: the focus word alone, with the frame shown around it (§4). Null
	// means the phrase never resolved a focus form (or hit the degenerate case where it covers every
	// token) — then the whole phrase is typed, exactly as before.
	//
	// {plan, full: every gapped form in reading order, first: the form the first-letters rung asks
	// for}. `full` is what the full-type rung accepts; a two-part separable verb types "hole ab".
	function typingPlan(card) {
		if (!card || !card.phrase || card.pos === VERB_FORM_POS || !card.focusForm || !gap()) { return null; }
		var plan = gap().plan(card.term, card.focusForm);
		if (!plan) { return null; }
		return { plan: plan, full: gap().expected(plan), first: plan.gaps[0] };
	}

	global.PhraseLearn = {
		mode: mode,
		collocOptions: collocOptions,
		meaningPlan: meaningPlan,
		gapPlan: gapPlan,
		filled: filled,
		typingPlan: typingPlan
	};
})(typeof window !== "undefined" ? window : globalThis);
